<template>
  <div class="with_sidebar">
    <LazyFilterSide v-if="Object.keys(store.filters)?.length" />
    <div class="main_wrap">
      <div class="container">
        <h1 class="page_title">{{ $t("heads.comparison.title") }}</h1>
        <div class="catalog">
          <LazyPagesComparisonItemComponentsTopFilters
              :all-items-count="paginationInfo?.totalCount || 0"
          />
          <div ref="scrollContainer" class="catalog_wrap">
            <LazyPagesComparisonItem :item="{} as CompareItem" is-header />
            <client-only>
              <LazyPagesComparisonItemPlaceholder
                  v-for="ph in 10"
                  v-if="pending"
                  :key="`placeholder-${ph}`"
              />
            </client-only>
            <template v-if="items?.length">
              <LazyPagesComparisonItem
                  v-for="(item, index) in items"
                  :key="item?.market_hash_name"
                  :index="index"
                  :is-header="false"
                  :item="item || ({} as CompareItem)"
              />
            </template>
            <client-only>
              <LazyPagesComparisonItemPlaceholder
                  v-for="ph in 10"
                  v-show="busy"
                  :key="`placeholder-${ph}`"
              />
            </client-only>
          </div>
          <LazyUiNoResult v-if="!items?.length && !pending && !busy" class="mxa mt50" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {getComparisonPageItems} from "~/utils/api";
import type {
  CompareItem,
  ComparePageSettings,
  ComparisonFiltersInput
} from "~/graphql/generated/schema-types";
import {ComparisonSort} from "~/graphql/generated/schema-types";
import {useComparisonStore} from "~/store/comparison";
import {EventBus} from "~/composables/bus";
import {useStore} from "~/store";

interface ComparisonFilters extends ComparisonFiltersInput {
  page?: number;
  sort?: ComparisonSort;
}

const {locale} = useI18n();
const route = useRoute();
const router = useRouter();
const store = useComparisonStore();
const globalStore = useStore();
const debounce = useDebounce();
const rate = computed(() => globalStore.rate);

const busy = ref(false);
const items = ref<CompareItem[]>([]);
const paginationInfo = ref<ComparePageSettings>({} as ComparePageSettings);
const oldPage = ref(+(route.query?.page || 1));
const scrollContainer = ref<HTMLElement>();
const isMounted = ref(false);
const abortController = ref(new AbortController());

store.initializeFilters();

const {data, error, pending} = await useAsyncData("getComparePageFilters", () =>
    getComparisonPageItems(
        {
          page: store.page,
          lang: locale.value,
          sort: store.sort,
          filter: store.activeFilters
        },
        rate.value
    )
);

const {data: presetsData, error: presetsErr} = await useAsyncData("getPresets", getComparisonPresets);
if (!presetsErr.value && presetsData.value?.data?.comparisonFilterTemplates) {
  store.presets = presetsData.value.data.comparisonFilterTemplates;
}

if (error.value || data.value?.errors?.length) {
  throw new Error(
      "Page comparison error" + (error.value?.message || data.value?.errors?.[0]?.message)
  );
}
items.value = (data.value?.data?.comparePageInfo?.items || []) as CompareItem[];
paginationInfo.value = (data.value?.data?.comparePageInfo?.settings || {}) as ComparePageSettings;

watch(
    [() => store.activeFilters, () => store.page, () => store.sort],
    debounce(async ([newFilters, filtersPage, filtersSort]) => {
      if (!isMounted.value) {
        return;
      }
      if (pending.value || busy.value) {
        abortController.value.abort();
        abortController.value = new AbortController();
      }
      busy.value = true;

      if (oldPage.value === filtersPage) {
        items.value = [];
        pending.value = true;
        store.page = 1;
        oldPage.value = 1;
        if (process.client) {
          window.scrollTo(0, 0);
        }
      } else {
        oldPage.value = filtersPage;
      }

      await router.replace({
        query: {
          ...stringifyQueryParams(
              cleanObject({
                ...newFilters,
                markets_from:
                    newFilters.markets_from?.length !== store.filters?.markets?.length
                        ? newFilters.markets_from
                        : null,
                markets_to:
                    newFilters.markets_to?.length !== store.filters?.markets?.length
                        ? newFilters.markets_to
                        : null,
                page: oldPage.value,
                sort: filtersSort
              }) as ComparisonFilters
          )
        }
      });
      const {data, errors} = await getComparisonPageItems(
          {
            page: filtersPage,
            lang: locale.value,
            sort: filtersSort,
            filter: store.activeFilters
          },
          rate.value,
          abortController.value.signal
      );

      busy.value = false;
      pending.value = false;
      if (errors?.length) {
        console.log(errors[0]?.message);
        items.value = [];
        EventBus.$emit("notify", {
          success: false,
          msg: errors[0]?.message || "An error occurred"
        });
        return;
      }
      paginationInfo.value = (data?.comparePageInfo?.settings || {}) as ComparePageSettings;
      items.value =
          filtersPage === 1
              ? ((data?.comparePageInfo?.items || []) as CompareItem[])
              : items.value.concat((data?.comparePageInfo?.items || []) as CompareItem[]);
    }, 800),
    {deep: true}
);

const checkScroll = async () => {
  const container = scrollContainer.value!;
  const rect = container?.getBoundingClientRect();

  if (
      rect.bottom - 100 <= window.innerHeight &&
      paginationInfo.value.hasNextPage &&
      !busy.value &&
      !pending.value
  ) {
    busy.value = true;
    store.page += 1;
  }
};

function stringifyQueryParams(params: ComparisonFilters): Record<string, string> {
  const result: Record<string, string> = {};

  for (const key in params) {
    if (
        params[key as keyof ComparisonFilters] !== undefined &&
        params[key as keyof ComparisonFilters] !== null
    ) {
      if (Array.isArray(params[key as keyof ComparisonFilters])) {
        result[key] = JSON.stringify(params[key as keyof ComparisonFilters]);
      } else {
        result[key] = String(params[key as keyof ComparisonFilters]);
      }
    }
  }

  return result;
}

onMounted(async () => {
  await store.fetchCompareFilters();
  await nextTick(() => {
    if (!route.query["markets_from"]) {
      store.activeFilters = {
        ...store.activeFilters,
        markets_from: (store.filters.markets?.map((i) => i?.market_name) || []) as string[]
      };
    }
    if (!route.query["markets_to"]) {
      store.activeFilters = {
        ...store.activeFilters,
        markets_to: (store.filters.markets?.map((i) => i?.market_name) || []) as string[]
      };
    }

    setTimeout(() => {
      isMounted.value = true;
    }, 1000);
  });

  document.addEventListener("scroll", checkScroll);
  //
  //   EventBus.$on('reset_filter', () => {
  //     router.push(localePath('/comparison'))
  //     store.activeFilters = getDefaultFilters()
  //   })
  EventBus.$on("comp_search", (data) => {
    store.activeFilters.search = data;
  });
});
//
onBeforeUnmount(() => {
  document?.removeEventListener("scroll", checkScroll);
  //   EventBus.$off('reset_filter')
  EventBus.$off("comp_search");
});
</script>

<style lang="scss" scoped>
.page_title {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.with_sidebar {
  display: flex;

  .main_wrap {
    width: 100%;
    padding-left: 31.5rem;

    .container {
      padding: 0 3rem;
      max-width: initial;
    }

    @media screen and (max-width: 1400px) {
      padding-left: 0;
      .container {
        padding: 0 16px;
      }
    }
  }
}

.catalog {
  margin: 0;
  padding-top: 5rem;

  &_wrap {
    //margin: 32px -3rem 0 0;
    //padding-right: 3rem;
    padding-left: 8px;
    //overflow-x: auto;
    @media screen and (max-width: 1200px) {
      padding: 0;
      margin: 0;
    }
    @media screen and (max-width: 1115px) {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }

  @media screen and (max-width: 979px) {
    padding-top: 2rem;
  }
}
</style>
